/**
 * Render IRGraph as a draw.io ER Diagram.
 * Uses draw.io's native table shape for entities and ER notation arrows.
 */

import type { LayoutResult, IRNode, ColorPalette } from '../types';
import { NodeShape } from '../types';
import { getShapeColors, applyColors } from '../color';
import { IdGen, esc, wrapMxfile, edgeCell, labelCell } from './xml-builder';
import type { ErAttribute } from '../parsers/mermaid-er';

const ENTITY_STYLE =
  'shape=table;startSize=30;container=1;collapsible=1;childLayout=tableLayout;fixedRows=1;rowLines=0;fontStyle=1;align=center;resizeLast=1;html=1;';

const ATTR_ROW_STYLE =
  'shape=partialRectangle;collapsible=0;dropTarget=0;pointerEvents=0;fillColor=none;points=[[0,0.5],[1,0.5]];portConstraint=eastwest;top=0;left=0;right=0;bottom=0;html=1;';

const ATTR_KEY_STYLE =
  'shape=partialRectangle;overflow=hidden;connectable=0;fillColor=none;top=0;left=0;right=0;bottom=0;fontStyle=1;html=1;';

const ATTR_NAME_STYLE =
  'shape=partialRectangle;overflow=hidden;connectable=0;fillColor=none;top=0;left=0;right=0;bottom=0;html=1;align=left;spacingLeft=6;';

const ATTR_TYPE_STYLE =
  'shape=partialRectangle;overflow=hidden;connectable=0;fillColor=none;top=0;left=0;right=0;bottom=0;html=1;align=left;spacingLeft=6;fontColor=#999999;';

export function renderDrawioEr(
  layout: LayoutResult,
  palette: ColorPalette,
): string {
  const gen = new IdGen();
  const graph = layout.graph;
  const cells: string[] = [];

  const nodeCellMap = new Map<string, string>();

  for (const [nid, node] of graph.nodes) {
    if (node.x === undefined || node.y === undefined) continue;
    if (node.shape !== NodeShape.ErEntity) continue;

    const attributes = (node.meta?.attributes ?? []) as ErAttribute[];

    // Entity container (table)
    const entityId = gen.next();
    nodeCellMap.set(nid, entityId);

    let style = ENTITY_STYLE;
    const colors = getShapeColors(node.shape, palette);
    style = applyColors(style, colors);

    const x = layout.containerX + (node.x ?? 0);
    const y = layout.containerY + (node.y ?? 0);

    cells.push(
      `        <mxCell id="${entityId}" value="${esc(node.label)}" ` +
      `style="${style}" vertex="1" parent="1">\n` +
      `          <mxGeometry x="${x}" y="${y}" ` +
      `width="${node.width}" height="${node.height}" as="geometry" />\n` +
      `        </mxCell>`,
    );

    // Attribute rows
    let rowY = 30;
    for (const attr of attributes) {
      const rowId = gen.next();
      const keyColId = gen.next();
      const nameColId = gen.next();
      const typeColId = gen.next();

      // Row container
      cells.push(
        `        <mxCell id="${rowId}" value="" ` +
        `style="${ATTR_ROW_STYLE}" vertex="1" parent="${entityId}">\n` +
        `          <mxGeometry y="${rowY}" width="${node.width}" height="22" as="geometry" />\n` +
        `        </mxCell>`,
      );

      // Key column (PK/FK/UK badge)
      const keyLabel = attr.key ?? '';
      cells.push(
        `        <mxCell id="${keyColId}" value="${esc(keyLabel)}" ` +
        `style="${ATTR_KEY_STYLE}" vertex="1" parent="${rowId}">\n` +
        `          <mxGeometry width="30" height="22" as="geometry" />\n` +
        `        </mxCell>`,
      );

      // Name column
      cells.push(
        `        <mxCell id="${nameColId}" value="${esc(attr.name)}" ` +
        `style="${ATTR_NAME_STYLE}" vertex="1" parent="${rowId}">\n` +
        `          <mxGeometry x="30" width="${Math.floor((node.width - 30) / 2)}" height="22" as="geometry" />\n` +
        `        </mxCell>`,
      );

      // Type column
      cells.push(
        `        <mxCell id="${typeColId}" value="${esc(attr.type)}" ` +
        `style="${ATTR_TYPE_STYLE}" vertex="1" parent="${rowId}">\n` +
        `          <mxGeometry x="${30 + Math.floor((node.width - 30) / 2)}" width="${Math.ceil((node.width - 30) / 2)}" height="22" as="geometry" />\n` +
        `        </mxCell>`,
      );

      rowY += 22;
    }
  }

  // Render relationship edges with ER cardinality arrows
  for (const edge of graph.edges) {
    const srcCell = nodeCellMap.get(edge.source);
    const tgtCell = nodeCellMap.get(edge.target);
    if (!srcCell || !tgtCell) continue;

    const extras = (edge.meta?.edgeStyleExtras as string) ?? '';
    const dashedStyle = edge.dashed ? 'dashed=1;' : '';
    const exitPt = edge.meta?.exitPoint as { x: number; y: number } | undefined;
    const entryPt = edge.meta?.entryPoint as { x: number; y: number } | undefined;
    let connPts = '';
    if (exitPt) connPts += `exitX=${exitPt.x};exitY=${exitPt.y};exitDx=0;exitDy=0;`;
    if (entryPt) connPts += `entryX=${entryPt.x};entryY=${entryPt.y};entryDx=0;entryDy=0;`;
    const style = `edgeStyle=orthogonalEdgeStyle;rounded=0;orthogonalLoop=1;jettySize=auto;html=1;${dashedStyle}${connPts}${extras}`;

    const edgeId = gen.next();
    const rawRoutePoints = edge.meta?.routePoints as Array<{ x: number; y: number }> | undefined;
    const routePoints = rawRoutePoints?.map(p => ({
      x: p.x + layout.containerX,
      y: p.y + layout.containerY,
    }));
    cells.push(edgeCell(edgeId, '', style, '1', srcCell, tgtCell, routePoints));

    if (edge.label) {
      const lblId = gen.next();
      cells.push(labelCell(lblId, edge.label, edgeId));
    }
  }

  return wrapMxfile(cells);
}
